/**
 * CONFIGURACIÓN SSO CENTRALIZADA - TODAS LAS APPS
 * 
 * Mapping centralizado de apps para flujo SSO handoff.
 * Incluye todas las apps del ecosistema.
 * 
 * @package @shared/navigation
 */

export interface AppSSOConfig {
  appName: string;
  devPort: number;
  devOrigin: string;
  prodDomain: string;
  defaultRoute: string;
}

/**
 * Configuración SSO para todas las apps del ecosistema
 */
export const APP_SSO_CONFIGS: Record<string, AppSSOConfig> = {
  empresa: {
    appName: 'empresa',
    devPort: 5173,
    devOrigin: 'http://localhost:5173',
    prodDomain: 'https://www.theplatform360.app',
    defaultRoute: '/empresa',
  },
  tienda: {
    appName: 'tienda',
    devPort: 5174,
    devOrigin: 'http://localhost:5174',
    prodDomain: 'https://tienda.elfom.com',
    defaultRoute: '/dashboard',
  },
  houseme: {
    appName: 'houseme',
    devPort: 5177,
    devOrigin: 'http://localhost:5177',
    prodDomain: 'https://houseme.app',
    defaultRoute: '/dashboard',
  },
  thefomlabs: {
    appName: 'thefomlabs',
    devPort: 5179,
    devOrigin: 'http://localhost:5179',
    prodDomain: 'https://thefomlabs.com',
    defaultRoute: '/cGxhdA/requests',
  },
  traela: {
    appName: 'traela',
    devPort: 5178,
    devOrigin: 'http://localhost:5178',
    prodDomain: 'https://traela.app',
    defaultRoute: '/dashboard',
  },
  vehicles: {
    appName: 'vehicles',
    devPort: 5180,
    devOrigin: 'http://localhost:5180',
    prodDomain: 'https://vehicles.elfom.com',
    defaultRoute: '/dashboard',
  },
  sonmany: {
    appName: 'sonmany',
    devPort: 5181,
    devOrigin: 'http://localhost:5181',
    prodDomain: 'https://sonmany.com',
    defaultRoute: '/dashboard',
  },
  gobscore: {
    appName: 'gobscore',
    devPort: 5183,
    devOrigin: 'http://localhost:5183',
    prodDomain: 'https://gobscore.org',
    defaultRoute: '/map',
  },
  'la-libreta': {
    appName: 'la-libreta',
    devPort: 5184,
    devOrigin: 'http://localhost:5184',
    prodDomain: 'https://lalibreta.app',
    defaultRoute: '/dashboard',
  },
  aroly: {
    appName: 'aroly',
    devPort: 5185,
    devOrigin: 'http://localhost:5185',
    prodDomain: 'https://aroly.app',
    defaultRoute: '/',
  },
  isela: {
    appName: 'isela',
    devPort: 5186,
    devOrigin: 'http://localhost:5186',
    prodDomain: 'https://isela.app',
    defaultRoute: '/',
  },
  pawtys: {
    appName: 'pawtys',
    devPort: 5180,
    devOrigin: 'http://localhost:5180',
    prodDomain: 'https://pawtys.app',
    defaultRoute: '/home',
  },
  orbitra: {
    appName: 'orbitra',
    devPort: 5187,
    devOrigin: 'http://localhost:5187',
    prodDomain: 'https://orbitrawork.com',
    defaultRoute: '/mi',
  },
};

/**
 * Obtiene la configuración SSO de una app
 * 
 * @param appName - Nombre de la app
 * @returns Configuración de la app o null si no existe
 */
export function getAppSSOConfig(appName: string | null | undefined): AppSSOConfig | null {
  if (!appName || !appName.trim()) {
    return null;
  }
  return APP_SSO_CONFIGS[appName.trim()] || null;
}

/**
 * Obtiene el default route para una app
 * 
 * @param appName - Nombre de la app
 * @returns Ruta por defecto o null si la app no existe
 */
export function getDefaultReturnTo(appName: string | null | undefined): string | null {
  const config = getAppSSOConfig(appName);
  return config?.defaultRoute || null;
}

/**
 * Detecta en tiempo de ejecución si debemos usar URLs de desarrollo (localhost).
 * Así, aunque Accounts se sirva en localhost con build de producción,
 * el retorno será a la app local (ej: localhost:5173) y no a producción.
 */
export function shouldUseDevOrigins(): boolean {
  if (typeof window === 'undefined') {
    return !!import.meta.env.DEV;
  }
  return window.location.hostname === 'localhost' || !!import.meta.env.DEV;
}

/**
 * Obtiene el origin (DEV o PROD) para una app
 * 
 * @param appName - Nombre de la app
 * @param isDev - Si es desarrollo (default: detección por localhost o import.meta.env.DEV)
 * @returns URL origin de la app o null si no existe
 */
export function getAppOrigin(appName: string | null | undefined, isDev?: boolean): string | null {
  const config = getAppSSOConfig(appName);
  if (!config) {
    return null;
  }
  const useDev = isDev ?? shouldUseDevOrigins();
  return useDev ? config.devOrigin : config.prodDomain;
}

/**
 * Origen confiable enviado por la app cliente (solo localhost / 127.0.0.1 y puerto DEV de esa app).
 * Evita open redirect: no acepta dominios arbitrarios.
 */
export function parseTrustedLocalOriginForApp(
  appName: string | null | undefined,
  hint: string | null | undefined,
): string | null {
  if (!hint?.trim() || !appName?.trim()) return null;
  const config = getAppSSOConfig(appName.trim());
  if (!config) return null;
  let u: URL;
  try {
    u = new URL(hint.trim());
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase();
  if (host !== 'localhost' && host !== '127.0.0.1') return null;
  const port = u.port || (u.protocol === 'https:' ? '443' : '80');
  if (port !== String(config.devPort)) return null;
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  return u.origin;
}

/**
 * URL base para /auth/callback tras handoff: respeta cliente local aunque Accounts sea producción.
 */
export function resolveHandoffAppBaseUrl(
  appName: string,
  clientOriginHint: string | null | undefined,
): string | null {
  const trusted = parseTrustedLocalOriginForApp(appName, clientOriginHint);
  if (trusted) return trusted;
  return getAppOrigin(appName, shouldUseDevOrigins());
}

/**
 * Obtiene el puerto DEV para una app
 * 
 * @param appName - Nombre de la app
 * @returns Puerto DEV o null si no existe
 */
export function getAppDevPort(appName: string | null | undefined): number | null {
  const config = getAppSSOConfig(appName);
  return config?.devPort || null;
}

/** Compara origins ignorando prefijo www y puerto implícito. */
function originsMatch(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    const strip = (h: string) => h.toLowerCase().replace(/^www\./, '');
    if (ua.protocol !== ub.protocol || strip(ua.hostname) !== strip(ub.hostname)) {
      return false;
    }
    const portA = ua.port || (ua.protocol === 'https:' ? '443' : '80');
    const portB = ub.port || (ub.protocol === 'https:' ? '443' : '80');
    return portA === portB;
  } catch {
    return false;
  }
}

/** Hostname → app (subdominios, vercel previews, www). */
function getAppFromHostname(hostname: string): string | null {
  const h = hostname.toLowerCase().replace(/^www\./, '');
  if (h.includes('houseme')) return 'houseme';
  if (h.includes('thefomlabs')) return 'thefomlabs';
  if (h.includes('theplatform360') || h.includes('empresa')) return 'empresa';
  if (h.includes('gobscore')) return 'gobscore';
  if (h.includes('lalibreta') || h.includes('la-libreta')) return 'la-libreta';
  if (h.includes('tienda')) return 'tienda';
  if (h.includes('traela')) return 'traela';
  if (h.includes('vehicles') || h.includes('alory')) return 'vehicles';
  if (h.includes('sonmany')) return 'sonmany';
  if (h.includes('orbitrawork') || h.includes('orbitra')) return 'orbitra';
  return null;
}

/**
 * Obtiene el nombre de la app desde su origin (reverse lookup)
 * Usa shouldUseDevOrigins() por defecto (localhost con build PROD → puertos DEV).
 */
export function getAppFromOrigin(origin: string, isDev?: boolean): string | null {
  const useDev = isDev ?? shouldUseDevOrigins();

  for (const [appName, config] of Object.entries(APP_SSO_CONFIGS)) {
    const expectedOrigin = useDev ? config.devOrigin : config.prodDomain;
    if (originsMatch(origin, expectedOrigin)) {
      return appName;
    }
  }

  try {
    return getAppFromHostname(new URL(origin).hostname);
  } catch {
    return null;
  }
}

/**
 * Detecta la app desde pathname + origin + hostname (síncrono, primer render).
 * Usado en AuthCallbackPage para splash y fallback de returnTo correctos por app.
 */
export function detectAppFromLocation(
  loc: Pick<Location, 'pathname' | 'origin' | 'hostname'> = window.location,
): string | null {
  const appType = import.meta.env.VITE_APP_TYPE?.trim();
  if (appType && APP_SSO_CONFIGS[appType]) {
    return appType;
  }

  const path = loc.pathname.toLowerCase();
  if (path.includes('/houseme')) return 'houseme';
  if (path.includes('/empresa')) return 'empresa';
  if (path.includes('/tienda')) return 'tienda';
  if (path.includes('/traela')) return 'traela';
  if (path.includes('/thefomlabs')) return 'thefomlabs';
  if (path.includes('/vehicles') || path.includes('/alory')) return 'vehicles';
  if (path.includes('/sonmany')) return 'sonmany';
  if (path.includes('/gobscore')) return 'gobscore';
  if (path.includes('/la-libreta') || path.includes('lalibreta')) return 'la-libreta';
  if (path.includes('/aroly')) return 'aroly';
  if (path.includes('/isela')) return 'isela';
  if (path.includes('/pawtys')) return 'pawtys';

  const fromOrigin = getAppFromOrigin(loc.origin);
  if (fromOrigin) return fromOrigin;

  return getAppFromHostname(loc.hostname);
}

/**
 * Resuelve returnTo post-handoff: '/' → defaultRoute de la app destino.
 */
export function resolveHandoffReturnTo(
  returnTo: string | null | undefined,
  appName: string,
): string {
  const defaultRoute = getDefaultReturnTo(appName) ?? '/';
  if (!returnTo?.trim() || returnTo.trim() === '/') {
    return defaultRoute;
  }
  const trimmed = returnTo.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return defaultRoute;
  }
  const pathOnly = trimmed.split('?')[0].split('#')[0].toLowerCase();
  if (pathOnly === '/auth/callback' || pathOnly.startsWith('/auth/callback/')) {
    return defaultRoute;
  }
  if (pathOnly === '/login' || pathOnly.startsWith('/login/')) {
    return defaultRoute;
  }
  return trimmed;
}
